import {
  createContext,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import { readSelectionScreenRect } from "../canvas-object-element";
import { useCtrlWheelZoom } from "../input/use-ctrl-wheel-zoom";
import { useCanvasDocStore } from "../store/canvas-doc-store";
import { useCanvasStore } from "../store/canvas-store";
import { useConnectorDragStore } from "../store/connector-drag-store";
import {
  resolveSelectionPanelPlacement,
  resolveSelectionPanelSafeArea,
  type SelectionPanelPlacement,
} from "./selection-panel-placement";

interface SelectionPanelPlacementState extends SelectionPanelPlacement {
  readonly maxWidth: number;
  readonly maxHeight: number;
  readonly settled: boolean;
}

/** 子のポップオーバーへ、パネルの配置が変わったことを伝える。 */
export const SelectionPanelPlacementContext = createContext<SelectionPanelPlacement | null>(null);

function createInitialPlacement(): SelectionPanelPlacementState {
  const safeArea = resolveSelectionPanelSafeArea({
    width: window.innerWidth,
    height: window.innerHeight,
  });
  return {
    side: "top",
    left: safeArea.left,
    top: safeArea.top,
    maxWidth: safeArea.width,
    maxHeight: safeArea.height,
    settled: false,
  };
}

function placementsEqual(
  previous: SelectionPanelPlacementState,
  next: SelectionPanelPlacementState
): boolean {
  return (
    previous.side === next.side &&
    previous.left === next.left &&
    previous.top === next.top &&
    previous.maxWidth === next.maxWidth &&
    previous.maxHeight === next.maxHeight &&
    previous.settled === next.settled
  );
}

export function SelectionStylePanelFrame({
  children,
}: {
  readonly children: ReactNode;
}): JSX.Element | null {
  const selectedObjectIds = useCanvasStore((state) => state.selectedObjectIds);
  const marqueeActive = useCanvasStore((state) => state.marqueeRect !== null);
  // 接続矢印を伸ばしている間も隠す。パネルは選択の近傍に浮くため、ドラッグの視界を遮るうえ、
  // パネルの上で離すと「キャンバス面ではない」と判定されてコネクタ先のオブジェクト自動作成が
  // 抑止されてしまう（canvas-surface-hit.ts）。
  const connectionDraftActive = useCanvasStore((state) => state.connectionDraft !== null);
  const view = useCanvasStore((state) => state.view);
  const objects = useCanvasDocStore((state) => state.objects);
  const directManipulationActive = useConnectorDragStore((state) =>
    selectedObjectIds.some((objectId) => state.frames[objectId] !== undefined)
  );
  const frameRef = useRef<HTMLDivElement | null>(null);
  const [anchorAvailable, setAnchorAvailable] = useState(true);
  const [placement, setPlacement] = useState<SelectionPanelPlacementState>(createInitialPlacement);
  useCtrlWheelZoom(frameRef);

  const updatePlacement = useCallback((): void => {
    // objects と view は、実描画矩形を読み直す契機として購読する。
    void objects;
    void view;
    const anchor = readSelectionScreenRect(selectedObjectIds);
    if (!anchor) {
      setAnchorAvailable(false);
      return;
    }

    // 子は通常フロー外の overlay として親子ラッパーへ絶対配置されるため、この外枠の実寸は
    // 親パネルの実寸と一致する。子の開閉・切替・実寸変化はここへ入力されない。
    const frame = frameRef.current;
    const panel = {
      width: frame?.offsetWidth ?? 0,
      height: frame?.offsetHeight ?? 0,
    };
    const viewport = { width: window.innerWidth, height: window.innerHeight };
    const safeArea = resolveSelectionPanelSafeArea(viewport);
    const nextPlacement = resolveSelectionPanelPlacement({ anchor, panel, viewport });
    const next: SelectionPanelPlacementState = {
      ...nextPlacement,
      maxWidth: safeArea.width,
      maxHeight: safeArea.height,
      settled: true,
    };
    setAnchorAvailable(true);
    setPlacement((previous) => (placementsEqual(previous, next) ? previous : next));
  }, [objects, selectedObjectIds, view]);

  // DOM の描画結果を読むため、通常の effect ではなく paint 前の layout effect で配置する。
  useLayoutEffect(updatePlacement);

  useEffect(() => {
    const onResize = (): void => updatePlacement();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [updatePlacement]);

  useEffect(() => {
    const frame = frameRef.current;
    if (!frame || typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(updatePlacement);
    observer.observe(frame);
    return () => observer.disconnect();
  }, [updatePlacement]);

  const portalTarget = document.querySelector<HTMLElement>('[data-testid="canvas-root"]');
  if (!portalTarget || !anchorAvailable || directManipulationActive || marqueeActive) return null;
  if (connectionDraftActive) return null;

  return createPortal(
    <SelectionPanelPlacementContext.Provider value={placement}>
      <div
        ref={frameRef}
        className="canvas-selection-style-panel"
        data-testid="selection-style-panel"
        data-side={placement.side}
        style={{
          left: `${placement.left}px`,
          top: `${placement.top}px`,
          maxWidth: `${placement.maxWidth}px`,
          maxHeight: `${placement.maxHeight}px`,
          visibility: placement.settled ? "visible" : "hidden",
        }}
      >
        {children}
      </div>
    </SelectionPanelPlacementContext.Provider>,
    portalTarget
  );
}
